import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { Collision, CollisionDetection, DragEndEvent, DragOverEvent, DragStartEvent } from '@dnd-kit/core';
import {
  AssignmentTurnedInOutlined,
  BlockOutlined,
  BusinessOutlined,
  CalendarTodayOutlined,
  DragIndicator,
  DriveFileMoveOutlined,
  FolderOutlined,
  KeyboardArrowDown,
  LinkOutlined,
  PersonOutlineOutlined,
  UploadFileOutlined,
} from '@mui/icons-material';
import Collapse from '@mui/material/Collapse';
import type { CompanyDocuments, DocumentReferencesView, FirmDocumentReference } from 'api';
import { FileTypeIcon, OverflowTooltip, Skeleton } from 'components/ui';
import { useSearchableListKeyboardNavigation } from 'components/ui/hooks';
import {
  isKeyboardActivationKey,
  KEYBOARD_ARROW_LEFT_KEY,
  KEYBOARD_ARROW_RIGHT_KEY,
  KEYBOARD_ENTER_KEY,
  KEYBOARD_SPACE_KEY,
} from 'components/ui/utilities/keyboardNavigation';
import { getSearchHighlightSegments } from 'components/ui/utilities/searchHighlight';
import { getCssPx } from 'components/ui/utilities/zoom';
import { useZoom } from 'context/ZoomContext';
import FirmDocumentFileActions from 'pages/Documents/FirmDocuments/components/FirmDocumentFileActions';
import FirmDocumentFolderActions from 'pages/Documents/FirmDocuments/components/FirmDocumentFolderActions';
import FirmDocumentsSelectionCheckbox from 'pages/Documents/FirmDocuments/components/FirmDocumentsSelectionCheckbox';
import type { FirmDocumentTreeFolder } from 'pages/Documents/FirmDocuments/utilities';
import {
  buildFirmDocumentTree,
  getFirmDocumentReferenceFileName,
  getFirmDocumentReferenceLabels,
  getFirmDocumentRequestedFileLabels,
  getFirmDocumentUploadedByLabel,
  getFirmDocumentUploadedDateLabel,
} from 'pages/Documents/FirmDocuments/utilities';
import { shortDate } from 'utilities';
import {
  MEASUREMENT_DATE_DOCUMENTS_TREE_AI_FILE_ICON_SRC,
  MEASUREMENT_DATE_DOCUMENTS_TREE_COPY_TARGET_ID_ATTRIBUTE,
  MEASUREMENT_DATE_DOCUMENTS_TREE_COPY_TARGET_LABEL_ATTRIBUTE,
  MEASUREMENT_DATE_DOCUMENTS_TREE_COPY_TARGET_SELECTOR,
  MEASUREMENT_DATE_DOCUMENTS_TREE_GENERATED_SOURCE_VALUE,
  MEASUREMENT_DATE_DOCUMENTS_TREE_HIGHLIGHT_SCROLL_DELAY_MS,
  MEASUREMENT_DATE_DOCUMENTS_TREE_LABELS,
  MEASUREMENT_DATE_DOCUMENTS_TREE_MEASUREMENT_DATE_DROP_TARGET_LABEL_ATTRIBUTE,
  MEASUREMENT_DATE_DOCUMENTS_TREE_MEASUREMENT_DATE_DROP_TARGET_SELECTOR,
  MEASUREMENT_DATE_DOCUMENTS_TREE_ROOT_SELECTOR,
  MEASUREMENT_DATE_DOCUMENTS_TREE_ROW_DEPTH_ATTRIBUTE,
  MEASUREMENT_DATE_DOCUMENTS_TREE_ROW_SELECTOR,
} from './MeasurementDateDocumentsTree.constants';
import type { MeasurementDateDocumentsTreeProps } from './MeasurementDateDocumentsTree.props';
import {
  MeasurementDateDocumentsTreeAiFileIcon,
  MeasurementDateDocumentsTreeChildren,
  MeasurementDateDocumentsTreeDragHandle,
  MeasurementDateDocumentsTreeDragOverlayCard,
  MeasurementDateDocumentsTreeDragOverlaySubtitle,
  MeasurementDateDocumentsTreeDragOverlayText,
  MeasurementDateDocumentsTreeDragOverlayTitle,
  MeasurementDateDocumentsTreeDropTargetOverlay,
  MeasurementDateDocumentsTreeDropTargetOverlayIcon,
  MeasurementDateDocumentsTreeDropTargetOverlayText,
  MeasurementDateDocumentsTreeDropTargetOverlayTitle,
  MeasurementDateDocumentsTreeFileIcon,
  MeasurementDateDocumentsTreeFileMetadata,
  MeasurementDateDocumentsTreeFolderEmptyBadge,
  MeasurementDateDocumentsTreeFolderLabel,
  MeasurementDateDocumentsTreeFolderName,
  MeasurementDateDocumentsTreeFolderRow,
  MeasurementDateDocumentsTreeFolderToggleIcon,
  MeasurementDateDocumentsTreeGroup,
  MeasurementDateDocumentsTreeIcon,
  MeasurementDateDocumentsTreeMetadataChip,
  MeasurementDateDocumentsTreeRoot,
  MeasurementDateDocumentsTreeRootDropTarget,
  MeasurementDateDocumentsTreeRow,
  MeasurementDateDocumentsTreeSearchHighlight,
  MeasurementDateDocumentsTreeShowMoreButton,
  MeasurementDateDocumentsTreeSkeletonRow,
  MeasurementDateDocumentsTreeState,
  MeasurementDateDocumentsTreeStateActionButton,
  MeasurementDateDocumentsTreeStateActions,
  MeasurementDateDocumentsTreeStateDescription,
  MeasurementDateDocumentsTreeStateTitle,
  MeasurementDateDocumentsTreeText,
} from './MeasurementDateDocumentsTree.styles';
import { MEASUREMENT_DATE_DOCUMENTS_TREE_TEST_IDS } from './MeasurementDateDocumentsTree.testIds';

const EMPTY_SELECTED_DOCUMENT_REFERENCE_IDS = new Set<number>();

type MeasurementDateDocumentsTreeReference = DocumentReferencesView &
  Pick<Partial<FirmDocumentReference>, 'measurement_date' | 'requested_files' | 'source'>;

interface FirmDocumentMetadataItem {
  icon: ReactNode;
  key: string;
  label: string;
  value: string;
}

type FirmDocumentDragData = {
  documentReference: DocumentReferencesView;
  type: 'firm-document';
};

type FirmDocumentDropData = {
  targetCompanyId?: number | null;
  targetFolderId: number | null;
  targetLabel: string;
  targetMeasurementDateId: number;
  type: 'firm-document-drop-target';
};

type FirmDocumentDragDropAction = 'blocked' | 'copy' | 'move';

interface FirmDocumentMeasurementDateCopyTarget {
  targetMeasurementDateId: number;
  targetMeasurementDateLabel: string;
}

interface FirmDocumentMeasurementDatePointerTarget {
  action: Extract<FirmDocumentDragDropAction, 'blocked' | 'copy'>;
  copyTarget?: FirmDocumentMeasurementDateCopyTarget;
  targetLabel: string;
}

type FirmDocumentTreeCollisionPointer = Parameters<CollisionDetection>[0]['pointerCoordinates'];
type FirmDocumentTreeDroppableContainers = Parameters<CollisionDetection>[0]['droppableContainers'];

const FIRM_DOCUMENT_DROP_TARGET_DEPTH_ATTRIBUTE = 'data-firm-documents-drop-target-depth';
const FIRM_DOCUMENT_DROP_TARGET_ID_ATTRIBUTE = 'data-firm-documents-drop-target-id';
const FIRM_DOCUMENT_DROP_TARGET_KIND_ATTRIBUTE = 'data-firm-documents-drop-target-kind';
const FIRM_DOCUMENT_DROP_TARGET_DEPTH_WEIGHT = 100_000_000;
const FIRM_DOCUMENT_DROP_TARGET_ROOT_KIND = 'root';

const getDropTargetDepth = (node: HTMLElement) => {
  const depth = Number(node.getAttribute(FIRM_DOCUMENT_DROP_TARGET_DEPTH_ATTRIBUTE));

  return Number.isFinite(depth) ? depth : 0;
};

const getDroppableContainerById = (droppableContainers: FirmDocumentTreeDroppableContainers, targetId: string) =>
  droppableContainers.find(droppableContainer => String(droppableContainer.id) === targetId);

const getLivePointerCollisions = (
  droppableContainers: FirmDocumentTreeDroppableContainers,
  pointerCoordinates: FirmDocumentTreeCollisionPointer
): Collision[] => {
  if (!pointerCoordinates || typeof document === 'undefined') {
    return [];
  }

  const visitedDropTargetIds = new Set<string>();

  return document
    .elementsFromPoint(pointerCoordinates.x, pointerCoordinates.y)
    .reduce<Collision[]>((collisions, element) => {
      const node = element.closest<HTMLElement>(`[${FIRM_DOCUMENT_DROP_TARGET_ID_ATTRIBUTE}]`);
      const targetId = node?.getAttribute(FIRM_DOCUMENT_DROP_TARGET_ID_ATTRIBUTE);

      if (!node || !targetId || visitedDropTargetIds.has(targetId)) {
        return collisions;
      }

      visitedDropTargetIds.add(targetId);
      const droppableContainer = getDroppableContainerById(droppableContainers, targetId);

      if (!droppableContainer || droppableContainer.disabled) {
        return collisions;
      }

      const rect = node.getBoundingClientRect();
      const area = rect.width * rect.height;
      const depth = getDropTargetDepth(node);
      const isRootDropTarget =
        node.getAttribute(FIRM_DOCUMENT_DROP_TARGET_KIND_ATTRIBUTE) === FIRM_DOCUMENT_DROP_TARGET_ROOT_KIND;
      const depthScore = isRootDropTarget ? -1 : depth;

      collisions.push({
        data: {
          droppableContainer,
          // Prefer deeper nested folders over their parent containers, then smaller live rects.
          value: depthScore * FIRM_DOCUMENT_DROP_TARGET_DEPTH_WEIGHT - area,
        },
        id: droppableContainer.id,
      });

      return collisions;
    }, [])
    .sort((firstCollision, secondCollision) => {
      const firstValue = firstCollision.data?.value ?? Number.NEGATIVE_INFINITY;
      const secondValue = secondCollision.data?.value ?? Number.NEGATIVE_INFINITY;

      return secondValue - firstValue;
    });
};

const getCollisionVerticalDistance = (collision: Collision, pointerCoordinates: FirmDocumentTreeCollisionPointer) => {
  const collisionRect = collision.data?.droppableContainer?.rect.current;

  if (!pointerCoordinates || !collisionRect) {
    return Number.POSITIVE_INFINITY;
  }

  return Math.abs(pointerCoordinates.y - (collisionRect.top + collisionRect.height / 2));
};

const sortFirmDocumentTreeCollisionsByPointerIntent = (
  collisions: Collision[],
  pointerCoordinates: FirmDocumentTreeCollisionPointer
) => {
  if (!pointerCoordinates || collisions.length < 2) {
    return collisions;
  }

  if (typeof collisions[0]?.data?.value === 'number') {
    return collisions;
  }

  return [...collisions].sort(
    (firstCollision, secondCollision) =>
      getCollisionVerticalDistance(firstCollision, pointerCoordinates) -
      getCollisionVerticalDistance(secondCollision, pointerCoordinates)
  );
};

const firmDocumentTreeCollisionDetection: CollisionDetection = args => {
  const livePointerCollisions = getLivePointerCollisions(args.droppableContainers, args.pointerCoordinates);

  return sortFirmDocumentTreeCollisionsByPointerIntent(livePointerCollisions, args.pointerCoordinates);
};

const getDocumentDraggableId = (documentReferenceId: number) => `firm-document-reference-${documentReferenceId}`;

const renderHighlightedFileName = (fileName: string, searchHighlightText: string) => {
  const segments = getSearchHighlightSegments(fileName, searchHighlightText);

  if (!segments.some(segment => segment.isHighlighted)) {
    return fileName;
  }

  return segments.map(segment => {
    if (!segment.isHighlighted) {
      return segment.text;
    }

    return (
      <MeasurementDateDocumentsTreeSearchHighlight key={`${segment.startIndex}-${segment.text}`}>
        {segment.text}
      </MeasurementDateDocumentsTreeSearchHighlight>
    );
  });
};

const getFolderDroppableId = (folderId: number) => `firm-document-folder-${folderId}`;
const getRootDroppableId = (measurementDateId: number) => `firm-document-root-${measurementDateId}`;

const getDropTargetPath = (parentPath: string | null | undefined, targetName: string) =>
  parentPath ? `${parentPath} > ${targetName}` : targetName;

const getFolderDropTargetLabel = ({
  folder,
  folderCompanyName,
  folderDisplayPath,
  measurementDateLabel,
}: {
  folder: FirmDocumentTreeFolder;
  folderCompanyName?: string | null;
  folderDisplayPath: string;
  measurementDateLabel?: string | null;
}) => {
  if (folder.isCompanyGroup) {
    return measurementDateLabel ? `${folder.name} - ${measurementDateLabel}` : folder.name;
  }

  if (folderCompanyName) {
    return getDropTargetPath(folderCompanyName, folderDisplayPath);
  }

  return folderDisplayPath;
};

const getMeasurementDateDropTargetLabel = (documents?: CompanyDocuments | null) => {
  const measurementDate = documents?.measurement_date;

  return measurementDate?.date
    ? (shortDate(measurementDate.date) ?? measurementDate.name)
    : (measurementDate?.name ?? null);
};

const getRootDropTargetLabel = (documents?: CompanyDocuments | null) => {
  const measurementDateLabel = getMeasurementDateDropTargetLabel(documents);

  if (!measurementDateLabel) {
    return MEASUREMENT_DATE_DOCUMENTS_TREE_LABELS.rootDropTargetLabel;
  }

  return measurementDateLabel;
};

const isFirmDocumentDragData = (value: unknown): value is FirmDocumentDragData =>
  Boolean(value) &&
  typeof value === 'object' &&
  (value as FirmDocumentDragData).type === 'firm-document' &&
  Boolean((value as FirmDocumentDragData).documentReference);

const isFirmDocumentDropData = (value: unknown): value is FirmDocumentDropData =>
  Boolean(value) &&
  typeof value === 'object' &&
  (value as FirmDocumentDropData).type === 'firm-document-drop-target' &&
  ((value as FirmDocumentDropData).targetFolderId === null ||
    typeof (value as FirmDocumentDropData).targetFolderId === 'number') &&
  typeof (value as FirmDocumentDropData).targetLabel === 'string' &&
  typeof (value as FirmDocumentDropData).targetMeasurementDateId === 'number';

const getMeasurementDateCopyTargetFromPoint = (
  ownerDocument: Document,
  x: number,
  y: number
): FirmDocumentMeasurementDateCopyTarget | null => {
  const targetElement = ownerDocument
    .elementsFromPoint(x, y)
    .find(
      element => element instanceof HTMLElement && element.matches(MEASUREMENT_DATE_DOCUMENTS_TREE_COPY_TARGET_SELECTOR)
    ) as HTMLElement | undefined;

  if (!targetElement) {
    return null;
  }

  const targetMeasurementDateId = Number(
    targetElement.getAttribute(MEASUREMENT_DATE_DOCUMENTS_TREE_COPY_TARGET_ID_ATTRIBUTE)
  );
  const targetMeasurementDateLabel = targetElement.getAttribute(
    MEASUREMENT_DATE_DOCUMENTS_TREE_COPY_TARGET_LABEL_ATTRIBUTE
  );

  if (!targetMeasurementDateId || !targetMeasurementDateLabel) {
    return null;
  }

  return {
    targetMeasurementDateId,
    targetMeasurementDateLabel,
  };
};

const getUnavailableMeasurementDateDropTargetFromPoint = (ownerDocument: Document, x: number, y: number) => {
  const targetElement = ownerDocument
    .elementsFromPoint(x, y)
    .find(
      element =>
        element instanceof HTMLElement &&
        element.matches(MEASUREMENT_DATE_DOCUMENTS_TREE_MEASUREMENT_DATE_DROP_TARGET_SELECTOR)
    ) as HTMLElement | undefined;

  return (
    targetElement?.getAttribute(MEASUREMENT_DATE_DOCUMENTS_TREE_MEASUREMENT_DATE_DROP_TARGET_LABEL_ATTRIBUTE) ?? null
  );
};

const getDocumentReferenceMeasurementDateId = (
  documentReference: DocumentReferencesView,
  fallbackMeasurementDateId?: number | null
) =>
  (documentReference as MeasurementDateDocumentsTreeReference).measurement_date?.id ??
  fallbackMeasurementDateId ??
  null;

const isAiDocumentReference = (documentReference: MeasurementDateDocumentsTreeReference) =>
  Boolean(documentReference.ai_classification) ||
  documentReference.source?.value === MEASUREMENT_DATE_DOCUMENTS_TREE_GENERATED_SOURCE_VALUE;

const getFileMetadataItems = (documentReference: MeasurementDateDocumentsTreeReference) => {
  const uploadedByLabel = getFirmDocumentUploadedByLabel(documentReference);
  const uploadedDateLabel = getFirmDocumentUploadedDateLabel(documentReference.file.uploaded_date);
  const referenceLabels = getFirmDocumentReferenceLabels(documentReference);
  const requestedFileLabels = getFirmDocumentRequestedFileLabels(documentReference);

  const metadataItems: Array<FirmDocumentMetadataItem | null> = [
    uploadedByLabel
      ? { icon: <PersonOutlineOutlined fontSize="inherit" />, key: 'source', label: 'Source', value: uploadedByLabel }
      : null,
    uploadedDateLabel
      ? {
        icon: <CalendarTodayOutlined fontSize="inherit" />,
        key: 'uploaded',
        label: 'Uploaded',
        value: uploadedDateLabel,
      }
      : null,
    referenceLabels.length
      ? {
        icon: <LinkOutlined fontSize="inherit" />,
        key: 'references',
        label: 'References',
        value: referenceLabels.join(', '),
      }
      : null,
    requestedFileLabels.length
      ? {
        icon: <AssignmentTurnedInOutlined fontSize="inherit" />,
        key: 'requests',
        label: 'Requests',
        value: requestedFileLabels.join(', '),
      }
      : null,
  ];

  return metadataItems.filter((item): item is FirmDocumentMetadataItem => Boolean(item));
};

const getFolderDocumentReferenceIds = (folder: FirmDocumentTreeFolder): number[] => [
  ...folder.files.map(documentReference => documentReference.document_reference_id),
  ...folder.subFolders.flatMap(getFolderDocumentReferenceIds),
];

const focusTreeRow = (row: HTMLElement | null | undefined) => {
  if (!row) {
    return false;
  }

  row.focus();
  row.scrollIntoView({ block: 'nearest' });
  return true;
};

const focusFirstChildTreeRow = (childrenId: string) =>
  focusTreeRow(
    document.getElementById(childrenId)?.querySelector<HTMLElement>(MEASUREMENT_DATE_DOCUMENTS_TREE_ROW_SELECTOR)
  );

const focusParentTreeRow = (currentRow: HTMLElement | null, depth: number) => {
  const root = currentRow?.closest<HTMLElement>(MEASUREMENT_DATE_DOCUMENTS_TREE_ROOT_SELECTOR);

  if (!currentRow || !root || depth <= 0) {
    return false;
  }

  const rows = Array.from(root.querySelectorAll<HTMLElement>(MEASUREMENT_DATE_DOCUMENTS_TREE_ROW_SELECTOR));
  const currentIndex = rows.indexOf(currentRow);

  for (let index = currentIndex - 1; index >= 0; index -= 1) {
    const rowDepth = Number(rows[index].getAttribute(MEASUREMENT_DATE_DOCUMENTS_TREE_ROW_DEPTH_ATTRIBUTE));

    if (rowDepth < depth) {
      return focusTreeRow(rows[index]);
    }
  }

  return false;
};

const getFolderRowAriaLabel = (folder: FirmDocumentTreeFolder, isOpen: boolean) => {
  const hasFolderChildren = folder.files.length > 0 || folder.subFolders.length > 0;

  if (!hasFolderChildren && !folder.isCompanyGroup) {
    return MEASUREMENT_DATE_DOCUMENTS_TREE_LABELS.emptyFolderRow(folder.name);
  }

  if (folder.isCompanyGroup) {
    return isOpen
      ? MEASUREMENT_DATE_DOCUMENTS_TREE_LABELS.collapseCompanyGroup(folder.name)
      : MEASUREMENT_DATE_DOCUMENTS_TREE_LABELS.expandCompanyGroup(folder.name);
  }

  return isOpen
    ? MEASUREMENT_DATE_DOCUMENTS_TREE_LABELS.collapseFolder(folder.name)
    : MEASUREMENT_DATE_DOCUMENTS_TREE_LABELS.expandFolder(folder.name);
};

const MeasurementDateDocumentsTreeMetadataChipItem = ({
  icon,
  label,
  testId,
  value,
}: {
  icon: ReactNode;
  label: string;
  testId: string;
  value: string;
}) => {
  const valueRef = useRef<HTMLSpanElement | null>(null);
  const title = `${label}: ${value}`;
  const chip = (
    <MeasurementDateDocumentsTreeMetadataChip aria-label={title} data-testid={testId}>
      <span aria-hidden="true" data-firm-document-metadata-chip-icon="true">
        {icon}
      </span>
      <span ref={valueRef} data-firm-document-metadata-chip-value="true">
        {value}
      </span>
    </MeasurementDateDocumentsTreeMetadataChip>
  );

  return (
    <OverflowTooltip measureRef={valueRef} placement="bottom" title={title}>
      {chip}
    </OverflowTooltip>
  );
};

const MeasurementDateDocumentsTreeSkeleton = () => (
  <MeasurementDateDocumentsTreeGroup data-testid={MEASUREMENT_DATE_DOCUMENTS_TREE_TEST_IDS.skeleton}>
    {Array.from({ length: MEASUREMENT_DATE_DOCUMENTS_TREE_LABELS.skeletonRowCount }, (_, index) => (
      <MeasurementDateDocumentsTreeSkeletonRow key={index} depth={index % 3 === 0 ? 0 : 1}>
        <Skeleton height={getCssPx(16)} radius={getCssPx(4)} width={getCssPx(16)} />
        <Skeleton height={getCssPx(16)} radius={getCssPx(4)} width={getCssPx(16)} />
        <Skeleton height={getCssPx(16)} radius={getCssPx(4)} width={getCssPx(16)} />
        <Skeleton height={getCssPx(14)} variant="text" width={index % 2 === 0 ? '68%' : '48%'} />
        <Skeleton height={getCssPx(16)} radius={getCssPx(4)} width={getCssPx(index % 2 === 0 ? 152 : 104)} />
        <Skeleton height={getCssPx(16)} radius={getCssPx(4)} width={getCssPx(24)} />
      </MeasurementDateDocumentsTreeSkeletonRow>
    ))}
  </MeasurementDateDocumentsTreeGroup>
);

const MeasurementDateDocumentsTreeStateMessage = ({
  actions,
  description,
  testId,
  title,
}: {
  actions?: MeasurementDateDocumentsTreeProps['emptyActions'];
  description: MeasurementDateDocumentsTreeProps['emptyDescription'];
  testId: string;
  title: MeasurementDateDocumentsTreeProps['emptyTitle'];
}) => (
  <MeasurementDateDocumentsTreeState data-testid={testId}>
    <MeasurementDateDocumentsTreeStateTitle>{title}</MeasurementDateDocumentsTreeStateTitle>
    <MeasurementDateDocumentsTreeStateDescription>{description}</MeasurementDateDocumentsTreeStateDescription>
    {actions ? <MeasurementDateDocumentsTreeStateActions>{actions}</MeasurementDateDocumentsTreeStateActions> : null}
  </MeasurementDateDocumentsTreeState>
);

const MeasurementDateDocumentsTreeDragOverlaySubtitleContent = ({
  dropAction,
  dropTargetLabel,
}: {
  dropAction: FirmDocumentDragDropAction;
  dropTargetLabel?: string | null;
}) => {
  if (dropAction === 'blocked') {
    return MEASUREMENT_DATE_DOCUMENTS_TREE_LABELS.dragOverlayUnavailableDescription;
  }

  if (!dropTargetLabel) {
    return MEASUREMENT_DATE_DOCUMENTS_TREE_LABELS.dragOverlayDescription;
  }

  const actionLabel = dropAction === 'copy' ? 'Copy' : 'Move';

  return (
    <>
      {actionLabel} <strong>{MEASUREMENT_DATE_DOCUMENTS_TREE_LABELS.dropTargetOverlayFileLabel}</strong> to{' '}
      <strong>{dropTargetLabel}</strong>
    </>
  );
};

const MeasurementDateDocumentsTreeDragOverlayContent = ({
  documentReference,
  dropAction,
  dropTargetLabel,
  zoomLevel,
}: {
  documentReference: DocumentReferencesView;
  dropAction: FirmDocumentDragDropAction;
  dropTargetLabel?: string | null;
  zoomLevel: number;
}) => {
  const fileName = getFirmDocumentReferenceFileName(documentReference);
  const isAiDocument = isAiDocumentReference(documentReference);
  let icon = <FileTypeIcon fileType={documentReference.file.file_type} />;

  if (isAiDocument) {
    icon = (
      <MeasurementDateDocumentsTreeAiFileIcon
        alt=""
        aria-hidden="true"
        src={MEASUREMENT_DATE_DOCUMENTS_TREE_AI_FILE_ICON_SRC}
      />
    );
  }

  if (dropAction === 'blocked') {
    icon = <BlockOutlined fontSize="inherit" />;
  }

  return (
    <MeasurementDateDocumentsTreeDragOverlayCard
      aria-label={MEASUREMENT_DATE_DOCUMENTS_TREE_LABELS.fileMoveOverlayLabel}
      data-testid={MEASUREMENT_DATE_DOCUMENTS_TREE_TEST_IDS.fileDragOverlay}
      isDropBlocked={dropAction === 'blocked'}
      role="presentation"
      zoomLevel={zoomLevel}>
      <MeasurementDateDocumentsTreeFileIcon>{icon}</MeasurementDateDocumentsTreeFileIcon>
      <MeasurementDateDocumentsTreeDragOverlayText>
        <MeasurementDateDocumentsTreeDragOverlayTitle>{fileName}</MeasurementDateDocumentsTreeDragOverlayTitle>
        <MeasurementDateDocumentsTreeDragOverlaySubtitle>
          <MeasurementDateDocumentsTreeDragOverlaySubtitleContent
            dropAction={dropAction}
            dropTargetLabel={dropTargetLabel}
          />
        </MeasurementDateDocumentsTreeDragOverlaySubtitle>
      </MeasurementDateDocumentsTreeDragOverlayText>
    </MeasurementDateDocumentsTreeDragOverlayCard>
  );
};

const MeasurementDateDocumentsTreeDropTargetOverlayContent = ({ targetName }: { targetName: string }) => (
  <MeasurementDateDocumentsTreeDropTargetOverlayText>
    <MeasurementDateDocumentsTreeDropTargetOverlayIcon>
      <DriveFileMoveOutlined fontSize="inherit" />
    </MeasurementDateDocumentsTreeDropTargetOverlayIcon>
    <MeasurementDateDocumentsTreeDropTargetOverlayTitle
      aria-label={MEASUREMENT_DATE_DOCUMENTS_TREE_LABELS.dropTargetOverlayTitle(targetName)}>
      Release to move {MEASUREMENT_DATE_DOCUMENTS_TREE_LABELS.dropTargetOverlayFileLabel} to{' '}
      <strong>{targetName}</strong>
    </MeasurementDateDocumentsTreeDropTargetOverlayTitle>
  </MeasurementDateDocumentsTreeDropTargetOverlayText>
);

const MeasurementDateDocumentsTreeRootDropZone = ({
  isVisible,
  measurementDateId,
  targetLabel,
}: {
  isVisible: boolean;
  measurementDateId?: number | null;
  targetLabel: string;
}) => {
  const isEnabled = Boolean(isVisible && measurementDateId);
  const { isOver, setNodeRef } = useDroppable({
    data: {
      targetFolderId: null,
      targetLabel,
      targetMeasurementDateId: measurementDateId ?? 0,
      type: 'firm-document-drop-target',
    } satisfies FirmDocumentDropData,
    disabled: !isEnabled,
    id: getRootDroppableId(measurementDateId ?? 0),
  });

  if (!isEnabled) {
    return null;
  }

  return (
    <MeasurementDateDocumentsTreeRootDropTarget
      ref={setNodeRef}
      aria-label={MEASUREMENT_DATE_DOCUMENTS_TREE_LABELS.rootDropDescription(targetLabel)}
      data-firm-documents-drop-target="true"
      data-firm-documents-drop-target-depth={-1}
      data-firm-documents-drop-target-id={getRootDroppableId(measurementDateId ?? 0)}
      data-firm-documents-drop-target-kind={FIRM_DOCUMENT_DROP_TARGET_ROOT_KIND}
      data-testid={MEASUREMENT_DATE_DOCUMENTS_TREE_TEST_IDS.rootDropTarget}
      isDropTarget={isOver}
      role="presentation">
      {isOver ? (
        <MeasurementDateDocumentsTreeDropTargetOverlay isDropTarget>
          <MeasurementDateDocumentsTreeDropTargetOverlayContent targetName={targetLabel} />
        </MeasurementDateDocumentsTreeDropTargetOverlay>
      ) : null}
    </MeasurementDateDocumentsTreeRootDropTarget>
  );
};

const MeasurementDateDocumentsTreeFileRow = ({
  actionInProgress,
  activeDocumentReferenceId,
  canCopyDocuments,
  canDownloadDocuments,
  canManageDocuments,
  canMoveDocuments,
  canSelectDocuments,
  depth,
  documentReference,
  downloadingDocumentReferenceId,
  isHighlighted,
  isSelected,
  onCopy,
  onDelete,
  onDownload,
  onPreviewSelect,
  onRename,
  searchHighlightText,
  onToggle,
}: {
  actionInProgress?: boolean;
  activeDocumentReferenceId?: number | null;
  canCopyDocuments?: boolean;
  canDownloadDocuments?: boolean;
  canManageDocuments?: boolean;
  canMoveDocuments?: boolean;
  canSelectDocuments?: boolean;
  depth: number;
  documentReference: DocumentReferencesView;
  downloadingDocumentReferenceId?: number | null;
  isHighlighted?: boolean;
  isSelected: boolean;
  onCopy?: MeasurementDateDocumentsTreeProps['onDocumentCopy'];
  onDelete?: MeasurementDateDocumentsTreeProps['onDocumentDelete'];
  onDownload?: MeasurementDateDocumentsTreeProps['onDocumentDownload'];
  onPreviewSelect?: MeasurementDateDocumentsTreeProps['onDocumentPreviewSelect'];
  onRename?: MeasurementDateDocumentsTreeProps['onDocumentRename'];
  searchHighlightText: string;
  onToggle: (documentReferenceId: number, checked: boolean) => void;
}) => {
  const fileNameRef = useRef<HTMLElement | null>(null);
  const rowRef = useRef<HTMLDivElement | null>(null);
  const isDragDisabled = !canMoveDocuments || actionInProgress;
  const {
    attributes: dragAttributes,
    isDragging,
    listeners: dragListeners,
    setNodeRef: setDraggableNodeRef,
  } = useDraggable({
    data: {
      documentReference,
      type: 'firm-document',
    } satisfies FirmDocumentDragData,
    disabled: isDragDisabled,
    id: getDocumentDraggableId(documentReference.document_reference_id),
  });
  const setRowNodeRef = useCallback(
    (node: HTMLDivElement | null) => {
      rowRef.current = node;
      setDraggableNodeRef(node);
    },
    [setDraggableNodeRef]
  );
  const fileName = getFirmDocumentReferenceFileName(documentReference);
  const isPreviewSelected = activeDocumentReferenceId === documentReference.document_reference_id;
  const fileNameLabel = renderHighlightedFileName(fileName, searchHighlightText);
  const isAiDocument = isAiDocumentReference(documentReference);
  const metadataItems = getFileMetadataItems(documentReference);
  const shouldShowRowDetails = !isPreviewSelected;
  const fileNameContent = (
    <OverflowTooltip measureRef={fileNameRef} title={fileName}>
      <MeasurementDateDocumentsTreeText ref={fileNameRef}>{fileNameLabel}</MeasurementDateDocumentsTreeText>
    </OverflowTooltip>
  );
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === KEYBOARD_ARROW_LEFT_KEY) {
      if (focusParentTreeRow(event.currentTarget, depth)) {
        event.preventDefault();
      }
      return;
    }

    if (isKeyboardActivationKey(event.key)) {
      event.preventDefault();

      if (canSelectDocuments) {
        onToggle(documentReference.document_reference_id, !isSelected);
      }
    }
  };
  const handleRowClick = () => {
    onPreviewSelect?.(documentReference);
  };

  // Scroll copied or navigated file rows into view after their accordion has rendered.
  useEffect(() => {
    if (!isHighlighted || !rowRef.current) {
      return undefined;
    }

    const ownerWindow = rowRef.current.ownerDocument.defaultView;
    let animationFrame: number | null = null;
    const timeoutId = ownerWindow?.setTimeout(() => {
      animationFrame = ownerWindow.requestAnimationFrame(() => {
        rowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }, MEASUREMENT_DATE_DOCUMENTS_TREE_HIGHLIGHT_SCROLL_DELAY_MS);

    return () => {
      if (ownerWindow && timeoutId !== undefined) {
        ownerWindow.clearTimeout(timeoutId);
      }

      if (ownerWindow && animationFrame !== null) {
        ownerWindow.cancelAnimationFrame(animationFrame);
      }
    };
  }, [isHighlighted]);

  return (
    <MeasurementDateDocumentsTreeRow
      ref={setRowNodeRef}
      aria-level={depth + 1}
      aria-current={isHighlighted ? 'true' : undefined}
      aria-selected={isSelected}
      data-testid={MEASUREMENT_DATE_DOCUMENTS_TREE_TEST_IDS.file(documentReference.document_reference_id)}
      data-measurement-date-documents-tree-depth={depth}
      data-measurement-date-documents-tree-row="true"
      depth={depth}
      isDragging={isDragging}
      isHighlighted={isHighlighted}
      isSelected={isSelected || isPreviewSelected}
      onClick={handleRowClick}
      onKeyDown={handleKeyDown}
      role="treeitem"
      tabIndex={0}>
      <FirmDocumentsSelectionCheckbox
        checked={isSelected}
        data-testid={MEASUREMENT_DATE_DOCUMENTS_TREE_TEST_IDS.checkbox(documentReference.document_reference_id)}
        disabled={!canSelectDocuments}
        inputProps={{ 'aria-label': MEASUREMENT_DATE_DOCUMENTS_TREE_LABELS.selectFile(fileName) }}
        onClick={event => event.stopPropagation()}
        onKeyDown={event => {
          if (isKeyboardActivationKey(event.key)) {
            event.stopPropagation();
          }
        }}
        onChange={checked => {
          if (canSelectDocuments) {
            onToggle(documentReference.document_reference_id, checked);
          }
        }}
      />
      <MeasurementDateDocumentsTreeDragHandle
        aria-label={MEASUREMENT_DATE_DOCUMENTS_TREE_LABELS.dragHandle(fileName)}
        data-file-drag-handle="true"
        data-testid={MEASUREMENT_DATE_DOCUMENTS_TREE_TEST_IDS.fileDragHandle(documentReference.document_reference_id)}
        disabled={isDragDisabled}
        onClick={event => event.stopPropagation()}
        onKeyDown={event => {
          if (isKeyboardActivationKey(event.key)) {
            event.stopPropagation();
          }
        }}
        type="button"
        {...(!isDragDisabled ? dragAttributes : undefined)}
        {...(!isDragDisabled ? dragListeners : undefined)}>
        <DragIndicator fontSize="inherit" />
      </MeasurementDateDocumentsTreeDragHandle>
      <MeasurementDateDocumentsTreeFileIcon>
        {isAiDocument ? (
          <MeasurementDateDocumentsTreeAiFileIcon
            alt=""
            aria-hidden="true"
            src={MEASUREMENT_DATE_DOCUMENTS_TREE_AI_FILE_ICON_SRC}
          />
        ) : (
          <FileTypeIcon fileType={documentReference.file.file_type} />
        )}
      </MeasurementDateDocumentsTreeFileIcon>
      {fileNameContent}
      {shouldShowRowDetails && metadataItems.length ? (
        <MeasurementDateDocumentsTreeFileMetadata
          data-firm-document-file-metadata="true"
          data-testid={MEASUREMENT_DATE_DOCUMENTS_TREE_TEST_IDS.metadata(documentReference.document_reference_id)}>
          {metadataItems.map(item => (
            <MeasurementDateDocumentsTreeMetadataChipItem
              key={item.key}
              icon={item.icon}
              label={item.label}
              testId={MEASUREMENT_DATE_DOCUMENTS_TREE_TEST_IDS.metadataChip(
                documentReference.document_reference_id,
                item.key
              )}
              value={item.value}
            />
          ))}
        </MeasurementDateDocumentsTreeFileMetadata>
      ) : null}
      {shouldShowRowDetails ? (
        <FirmDocumentFileActions
          actionInProgress={actionInProgress}
          canCopy={Boolean(onCopy && canCopyDocuments)}
          canDelete={Boolean(canManageDocuments)}
          canDownload={Boolean(canDownloadDocuments)}
          canRename={Boolean(canManageDocuments)}
          documentReference={documentReference}
          fileName={fileName}
          isDownloadInProgress={downloadingDocumentReferenceId === documentReference.document_reference_id}
          onCopy={onCopy}
          onDelete={target => onDelete?.(target)}
          onDownload={target => onDownload?.(target)}
          onRename={target => onRename?.(target)}
        />
      ) : null}
    </MeasurementDateDocumentsTreeRow>
  );
};

const MeasurementDateDocumentsTreeFolder = ({
  actionInProgress,
  activeDocumentReferenceId,
  canCopyDocuments,
  canDownloadDocuments,
  canManageDocuments,
  canMoveDocuments,
  canSelectDocuments,
  depth,
  folder,
  downloadingDocumentReferenceId,
  closedFolderIds,
  folderPath,
  inheritedCompanyId,
  inheritedCompanyName,
  isOpen,
  onToggleDocument,
  onDocumentCopy,
  onToggleFolder,
  onToggleFolderDocuments,
  onDocumentDelete,
  onDocumentDownload,
  onDocumentPreviewSelect,
  onDocumentRename,
  onFolderAddSubfolder,
  onFolderDelete,
  onFolderRename,
  onFolderUploadDocument,
  highlightedDocumentReferenceId,
  highlightedFolderId,
  searchHighlightText,
  selectedDocumentReferenceIds,
  measurementDateLabel,
}: {
  actionInProgress?: boolean;
  activeDocumentReferenceId?: number | null;
  canCopyDocuments?: boolean;
  canDownloadDocuments?: boolean;
  canManageDocuments?: boolean;
  canMoveDocuments?: boolean;
  canSelectDocuments?: boolean;
  depth: number;
  downloadingDocumentReferenceId?: number | null;
  folderPath?: string | null;
  folder: FirmDocumentTreeFolder;
  closedFolderIds: Set<number>;
  inheritedCompanyId?: number | null;
  inheritedCompanyName?: string | null;
  isOpen: boolean;
  onDocumentDelete?: MeasurementDateDocumentsTreeProps['onDocumentDelete'];
  onDocumentDownload?: MeasurementDateDocumentsTreeProps['onDocumentDownload'];
  onDocumentPreviewSelect?: MeasurementDateDocumentsTreeProps['onDocumentPreviewSelect'];
  onDocumentCopy?: MeasurementDateDocumentsTreeProps['onDocumentCopy'];
  onDocumentRename?: MeasurementDateDocumentsTreeProps['onDocumentRename'];
  onFolderAddSubfolder?: MeasurementDateDocumentsTreeProps['onFolderAddSubfolder'];
  onFolderDelete?: MeasurementDateDocumentsTreeProps['onFolderDelete'];
  onFolderRename?: MeasurementDateDocumentsTreeProps['onFolderRename'];
  onFolderUploadDocument?: MeasurementDateDocumentsTreeProps['onFolderUploadDocument'];
  highlightedDocumentReferenceId?: number | null;
  highlightedFolderId?: number | null;
  searchHighlightText: string;
  onToggleDocument: (documentReferenceId: number, checked: boolean) => void;
  onToggleFolder: (folderId: number) => void;
  onToggleFolderDocuments: (documentReferenceIds: number[], checked: boolean) => void;
  selectedDocumentReferenceIds: Set<number>;
  measurementDateLabel?: string | null;
}) => {
  const folderRowRef = useRef<HTMLDivElement | null>(null);
  const folderNameRef = useRef<HTMLElement | null>(null);
  const folderCompanyId = folder.isCompanyGroup ? (folder.companyId ?? null) : (inheritedCompanyId ?? null);
  const folderCompanyName = folder.isCompanyGroup ? folder.name : (inheritedCompanyName ?? null);
  const targetFolderId = folder.isCompanyGroup ? null : folder.id;
  const targetMeasurementDateId = folder.measurementDateId ?? null;
  const folderDisplayPath = getDropTargetPath(folderPath, folder.name);
  const folderDropTargetLabel = getFolderDropTargetLabel({
    folder,
    folderCompanyName,
    folderDisplayPath,
    measurementDateLabel,
  });
  const overlayTargetLabel = folder.isCompanyGroup ? folderDropTargetLabel : folder.name;
  const childFolderPath = folder.isCompanyGroup ? null : folderDisplayPath;
  const isFolderDropTargetEnabled = Boolean(canMoveDocuments && targetMeasurementDateId);
  const { isOver, setNodeRef: setFolderDropTargetRef } = useDroppable({
    data: {
      targetCompanyId: folderCompanyId,
      targetFolderId,
      targetLabel: folderDropTargetLabel,
      targetMeasurementDateId: targetMeasurementDateId ?? 0,
      type: 'firm-document-drop-target',
    } satisfies FirmDocumentDropData,
    disabled: !isFolderDropTargetEnabled,
    id: getFolderDroppableId(folder.id),
  });
  const testIdKey = folder.companyId ?? folder.id;
  const childrenId = folder.isCompanyGroup
    ? MEASUREMENT_DATE_DOCUMENTS_TREE_TEST_IDS.companyGroupChildren(testIdKey)
    : MEASUREMENT_DATE_DOCUMENTS_TREE_TEST_IDS.folderChildren(folder.id);
  const folderDocumentReferenceIds = useMemo(() => getFolderDocumentReferenceIds(folder), [folder]);
  const hasFolderChildren = folder.files.length > 0 || folder.subFolders.length > 0;
  const selectedFolderDocumentReferenceCount = folderDocumentReferenceIds.filter(documentReferenceId =>
    selectedDocumentReferenceIds.has(documentReferenceId)
  ).length;
  const hasSelectableDocuments = folderDocumentReferenceIds.length > 0;
  const isFolderHighlighted = highlightedFolderId === folder.id;
  const canShowFolderActions = Boolean(
    canManageDocuments &&
      folder.md_documents &&
      (onFolderAddSubfolder || onFolderUploadDocument || (!folder.isCompanyGroup && onFolderDelete && onFolderRename))
  );
  const isFolderSelected =
    hasSelectableDocuments && selectedFolderDocumentReferenceCount === folderDocumentReferenceIds.length;
  const isFolderIndeterminate =
    selectedFolderDocumentReferenceCount > 0 &&
    selectedFolderDocumentReferenceCount < folderDocumentReferenceIds.length;

  // Scroll edited folder rows into view after their modal has closed and the tree has refreshed.
  useEffect(() => {
    if (!isFolderHighlighted || !folderRowRef.current) {
      return undefined;
    }

    const ownerWindow = folderRowRef.current.ownerDocument.defaultView;
    let animationFrame: number | null = null;
    const timeoutId = ownerWindow?.setTimeout(() => {
      animationFrame = ownerWindow.requestAnimationFrame(() => {
        folderRowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }, MEASUREMENT_DATE_DOCUMENTS_TREE_HIGHLIGHT_SCROLL_DELAY_MS);

    return () => {
      if (ownerWindow && timeoutId !== undefined) {
        ownerWindow.clearTimeout(timeoutId);
      }

      if (ownerWindow && animationFrame !== null) {
        ownerWindow.cancelAnimationFrame(animationFrame);
      }
    };
  }, [isFolderHighlighted]);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === KEYBOARD_ARROW_RIGHT_KEY) {
      event.preventDefault();

      if (!hasFolderChildren) {
        return;
      }

      if (!isOpen) {
        onToggleFolder(folder.id);
        return;
      }

      focusFirstChildTreeRow(childrenId);
      return;
    }

    if (event.key === KEYBOARD_ARROW_LEFT_KEY) {
      event.preventDefault();

      if (hasFolderChildren && isOpen) {
        onToggleFolder(folder.id);
        return;
      }

      focusParentTreeRow(event.currentTarget, depth);
      return;
    }

    if (event.key === KEYBOARD_SPACE_KEY) {
      event.preventDefault();

      if (canSelectDocuments && hasSelectableDocuments) {
        if (!isOpen) {
          onToggleFolder(folder.id);
        }

        onToggleFolderDocuments(folderDocumentReferenceIds, !isFolderSelected);
      }

      return;
    }

    if (event.key === KEYBOARD_ENTER_KEY) {
      event.preventDefault();

      if (hasFolderChildren) {
        onToggleFolder(folder.id);
      }
    }
  };
  const handleFolderRowClick = () => {
    if (hasFolderChildren) {
      onToggleFolder(folder.id);
    }
  };

  return (
    <MeasurementDateDocumentsTreeGroup
      ref={setFolderDropTargetRef}
      data-firm-documents-drop-target={isFolderDropTargetEnabled ? 'true' : undefined}
      data-firm-documents-drop-target-depth={depth}
      data-firm-documents-drop-target-id={getFolderDroppableId(folder.id)}>
      <MeasurementDateDocumentsTreeFolderRow
        ref={folderRowRef}
        aria-controls={hasFolderChildren ? childrenId : undefined}
        aria-current={isFolderHighlighted ? 'true' : undefined}
        aria-expanded={hasFolderChildren ? isOpen : undefined}
        aria-level={depth + 1}
        aria-label={getFolderRowAriaLabel(folder, isOpen)}
        data-testid={
          folder.isCompanyGroup
            ? MEASUREMENT_DATE_DOCUMENTS_TREE_TEST_IDS.companyGroup(testIdKey)
            : MEASUREMENT_DATE_DOCUMENTS_TREE_TEST_IDS.folder(folder.id)
        }
        data-measurement-date-documents-tree-depth={depth}
        data-measurement-date-documents-tree-row="true"
        depth={depth}
        isHighlighted={isFolderHighlighted}
        isInteractive={hasFolderChildren}
        onClick={handleFolderRowClick}
        onKeyDown={handleKeyDown}
        role="treeitem"
        tabIndex={0}>
        <FirmDocumentsSelectionCheckbox
          checked={isFolderSelected}
          data-testid={
            folder.isCompanyGroup
              ? MEASUREMENT_DATE_DOCUMENTS_TREE_TEST_IDS.companyGroupCheckbox(testIdKey)
              : MEASUREMENT_DATE_DOCUMENTS_TREE_TEST_IDS.folderCheckbox(folder.id)
          }
          disabled={!canSelectDocuments || !hasSelectableDocuments}
          indeterminate={isFolderIndeterminate}
          inputProps={{
            'aria-label': folder.isCompanyGroup
              ? MEASUREMENT_DATE_DOCUMENTS_TREE_LABELS.selectCompanyDocuments(folder.name)
              : MEASUREMENT_DATE_DOCUMENTS_TREE_LABELS.selectFolderDocuments(folder.name),
          }}
          onClick={event => event.stopPropagation()}
          onKeyDown={event => {
            if (isKeyboardActivationKey(event.key)) {
              event.stopPropagation();
            }
          }}
          onChange={checked => {
            if (!canSelectDocuments) {
              return;
            }

            if (!isOpen) {
              onToggleFolder(folder.id);
            }

            onToggleFolderDocuments(folderDocumentReferenceIds, checked);
          }}
        />
        <MeasurementDateDocumentsTreeIcon>
          {folder.isCompanyGroup ? <BusinessOutlined fontSize="inherit" /> : <FolderOutlined fontSize="inherit" />}
        </MeasurementDateDocumentsTreeIcon>
        <MeasurementDateDocumentsTreeFolderLabel>
          <OverflowTooltip measureRef={folderNameRef} title={folder.name}>
            <MeasurementDateDocumentsTreeFolderName ref={folderNameRef}>
              {folder.name}
            </MeasurementDateDocumentsTreeFolderName>
          </OverflowTooltip>
          {!hasFolderChildren ? (
            <MeasurementDateDocumentsTreeFolderEmptyBadge
              data-testid={MEASUREMENT_DATE_DOCUMENTS_TREE_TEST_IDS.folderEmptyState(folder.id)}>
              {MEASUREMENT_DATE_DOCUMENTS_TREE_LABELS.emptyFolderLabel}
            </MeasurementDateDocumentsTreeFolderEmptyBadge>
          ) : null}
        </MeasurementDateDocumentsTreeFolderLabel>
        {canShowFolderActions ? (
          <FirmDocumentFolderActions
            actionInProgress={actionInProgress}
            folder={folder}
            onAddSubfolder={onFolderAddSubfolder}
            onDelete={folder.isCompanyGroup ? undefined : targetFolder => onFolderDelete?.(targetFolder)}
            onRename={folder.isCompanyGroup ? undefined : targetFolder => onFolderRename?.(targetFolder)}
            onUploadDocument={onFolderUploadDocument}
          />
        ) : null}
        {hasFolderChildren ? (
          <MeasurementDateDocumentsTreeFolderToggleIcon
            aria-hidden="true"
            data-testid={
              folder.isCompanyGroup
                ? MEASUREMENT_DATE_DOCUMENTS_TREE_TEST_IDS.companyGroupToggle(testIdKey)
                : MEASUREMENT_DATE_DOCUMENTS_TREE_TEST_IDS.folderToggle(folder.id)
            }
            isExpanded={isOpen}
            tabIndex={-1}>
            <KeyboardArrowDown fontSize="inherit" />
          </MeasurementDateDocumentsTreeFolderToggleIcon>
        ) : null}
        {!hasFolderChildren && canShowFolderActions ? (
          <MeasurementDateDocumentsTreeFolderToggleIcon aria-hidden="true" isExpanded={false} />
        ) : null}
      </MeasurementDateDocumentsTreeFolderRow>
      <Collapse id={childrenId} in={hasFolderChildren && isOpen} timeout="auto" unmountOnExit>
        <MeasurementDateDocumentsTreeChildren data-testid={childrenId} depth={depth}>
          {folder.files.map(documentReference => (
            <MeasurementDateDocumentsTreeFileRow
              key={documentReference.document_reference_id}
              actionInProgress={actionInProgress}
              activeDocumentReferenceId={activeDocumentReferenceId}
              canCopyDocuments={canCopyDocuments}
              canDownloadDocuments={canDownloadDocuments}
              canManageDocuments={canManageDocuments}
              canMoveDocuments={canMoveDocuments}
              canSelectDocuments={canSelectDocuments}
              depth={depth + 1}
              documentReference={documentReference}
              downloadingDocumentReferenceId={downloadingDocumentReferenceId}
              isHighlighted={highlightedDocumentReferenceId === documentReference.document_reference_id}
              isSelected={selectedDocumentReferenceIds.has(documentReference.document_reference_id)}
              onCopy={onDocumentCopy}
              onDelete={onDocumentDelete}
              onDownload={onDocumentDownload}
              onPreviewSelect={onDocumentPreviewSelect}
              onRename={onDocumentRename}
              searchHighlightText={searchHighlightText}
              onToggle={onToggleDocument}
            />
          ))}
          {folder.subFolders.map(subFolder => (
            <MeasurementDateDocumentsTreeFolder
              key={subFolder.id}
              depth={depth + 1}
              folder={subFolder}
              actionInProgress={actionInProgress}
              activeDocumentReferenceId={activeDocumentReferenceId}
              canCopyDocuments={canCopyDocuments}
              canDownloadDocuments={canDownloadDocuments}
              canManageDocuments={canManageDocuments}
              canMoveDocuments={canMoveDocuments}
              canSelectDocuments={canSelectDocuments}
              downloadingDocumentReferenceId={downloadingDocumentReferenceId}
              folderPath={childFolderPath}
              inheritedCompanyId={folderCompanyId}
              inheritedCompanyName={folderCompanyName}
              highlightedDocumentReferenceId={highlightedDocumentReferenceId}
              highlightedFolderId={highlightedFolderId}
              closedFolderIds={closedFolderIds}
              isOpen={!closedFolderIds.has(subFolder.id)}
              onDocumentDelete={onDocumentDelete}
              onDocumentDownload={onDocumentDownload}
              onDocumentCopy={onDocumentCopy}
              onDocumentPreviewSelect={onDocumentPreviewSelect}
              onDocumentRename={onDocumentRename}
              onFolderAddSubfolder={onFolderAddSubfolder}
              onFolderDelete={onFolderDelete}
              onFolderRename={onFolderRename}
              onFolderUploadDocument={onFolderUploadDocument}
              onToggleDocument={onToggleDocument}
              onToggleFolder={onToggleFolder}
              onToggleFolderDocuments={onToggleFolderDocuments}
              searchHighlightText={searchHighlightText}
              selectedDocumentReferenceIds={selectedDocumentReferenceIds}
              measurementDateLabel={measurementDateLabel}
            />
          ))}
        </MeasurementDateDocumentsTreeChildren>
      </Collapse>
      {isFolderDropTargetEnabled && isOver ? (
        <MeasurementDateDocumentsTreeDropTargetOverlay aria-hidden="true" isDropTarget>
          <MeasurementDateDocumentsTreeDropTargetOverlayContent targetName={overlayTargetLabel} />
        </MeasurementDateDocumentsTreeDropTargetOverlay>
      ) : null}
    </MeasurementDateDocumentsTreeGroup>
  );
};

const MeasurementDateDocumentsTree = ({
  actionInProgress = false,
  activeDocumentReferenceId = null,
  canCopyDocuments = false,
  canDownloadDocuments = true,
  canManageDocuments = true,
  canMoveDocuments = false,
  canSelectDocuments = true,
  documents,
  downloadingDocumentReferenceId = null,
  emptyActions,
  emptyDescription = MEASUREMENT_DATE_DOCUMENTS_TREE_LABELS.emptyDescription,
  emptyTitle = MEASUREMENT_DATE_DOCUMENTS_TREE_LABELS.emptyTitle,
  errorDescription = MEASUREMENT_DATE_DOCUMENTS_TREE_LABELS.errorDescription,
  errorTitle = MEASUREMENT_DATE_DOCUMENTS_TREE_LABELS.errorTitle,
  hasMoreDocuments = false,
  highlightedDocumentReferenceId = null,
  highlightedFolderId = null,
  isError = false,
  isLoading = false,
  isShowMoreDocumentsLoading = false,
  onDocumentDelete,
  onDocumentDownload,
  onDocumentCopy,
  onDocumentCopyToMeasurementDate,
  onDocumentMove,
  onDocumentPreviewSelect,
  onDocumentRename,
  onDocumentReferencesSelectionChange,
  onDocumentSelectionChange,
  onFolderAddSubfolder,
  onFolderDelete,
  onFolderRename,
  onFolderUploadDocument,
  onEmptyUploadDocument,
  onShowMoreDocuments,
  ordering = null,
  remainingDocumentCount = 0,
  searchHighlightText = '',
  selectedDocumentReferenceIds = EMPTY_SELECTED_DOCUMENT_REFERENCE_IDS,
  showEmptyFolders = true,
}: MeasurementDateDocumentsTreeProps) => {
  const { zoomLevel } = useZoom();
  const treeRootRef = useRef<HTMLDivElement | null>(null);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 6,
      },
    })
  );
  const { handleItemsKeyDown } = useSearchableListKeyboardNavigation<HTMLDivElement>({
    itemSelector: MEASUREMENT_DATE_DOCUMENTS_TREE_ROW_SELECTOR,
    listRef: treeRootRef,
  });
  const documentTree = useMemo(
    () => buildFirmDocumentTree(documents, ordering, { showEmptyFolders }),
    [documents, ordering, showEmptyFolders]
  );
  const hasCompanyGroups = documentTree.folders.some(folder => folder.isCompanyGroup);
  const treeMeasurementDateId = documents?.measurement_date?.id ?? null;
  const measurementDateDropTargetLabel = useMemo(() => getMeasurementDateDropTargetLabel(documents), [documents]);
  const rootDropTargetLabel = useMemo(() => getRootDropTargetLabel(documents), [documents]);
  const [closedFolderIds, setClosedFolderIds] = useState<Set<number>>(() => new Set());
  const [activeDraggedDocument, setActiveDraggedDocument] = useState<DocumentReferencesView | null>(null);
  const [activeDropAction, setActiveDropActionState] = useState<FirmDocumentDragDropAction>('move');
  const [activeDropTargetLabel, setActiveDropTargetLabelState] = useState<string | null>(null);
  const activeDropActionRef = useRef<FirmDocumentDragDropAction>('move');
  const activeDropTargetLabelRef = useRef<string | null>(null);
  const dragPointerRef = useRef<{ x: number; y: number } | null>(null);
  const isDocumentMoveEnabled = Boolean(canManageDocuments && canMoveDocuments && onDocumentMove);
  const isDocumentMoveInteractionEnabled = isDocumentMoveEnabled && !actionInProgress;
  let emptyStateActions = emptyActions;

  const setActiveDropAction = useCallback((nextDropAction: FirmDocumentDragDropAction) => {
    if (activeDropActionRef.current === nextDropAction) {
      return;
    }

    activeDropActionRef.current = nextDropAction;
    setActiveDropActionState(nextDropAction);
  }, []);

  const setActiveDropTargetLabel = useCallback((nextDropTargetLabel: string | null) => {
    if (activeDropTargetLabelRef.current === nextDropTargetLabel) {
      return;
    }

    activeDropTargetLabelRef.current = nextDropTargetLabel;
    setActiveDropTargetLabelState(nextDropTargetLabel);
  }, []);

  if (emptyStateActions === undefined && onEmptyUploadDocument) {
    emptyStateActions = (
      <MeasurementDateDocumentsTreeStateActionButton
        data-testid={MEASUREMENT_DATE_DOCUMENTS_TREE_TEST_IDS.emptyUploadDocument}
        onClick={onEmptyUploadDocument}
        startIcon={<UploadFileOutlined fontSize="inherit" />}
        visualVariant="primaryGhost">
        {MEASUREMENT_DATE_DOCUMENTS_TREE_LABELS.uploadFirstDocument}
      </MeasurementDateDocumentsTreeStateActionButton>
    );
  }

  const getCurrentMeasurementDatePointerTarget = useCallback((): FirmDocumentMeasurementDatePointerTarget | null => {
    const point = dragPointerRef.current;
    const ownerDocument = treeRootRef.current?.ownerDocument;

    if (!point || !ownerDocument) {
      return null;
    }

    const copyTarget = onDocumentCopyToMeasurementDate
      ? getMeasurementDateCopyTargetFromPoint(ownerDocument, point.x, point.y)
      : null;

    if (copyTarget) {
      return {
        action: 'copy',
        copyTarget,
        targetLabel: copyTarget.targetMeasurementDateLabel,
      };
    }

    const unavailableTargetLabel = getUnavailableMeasurementDateDropTargetFromPoint(ownerDocument, point.x, point.y);

    return unavailableTargetLabel
      ? {
        action: 'blocked',
        targetLabel: unavailableTargetLabel,
      }
      : null;
  }, [onDocumentCopyToMeasurementDate]);

  // Reset folder disclosure whenever the active measurement-date document bucket changes.
  useEffect(() => {
    setClosedFolderIds(new Set());
  }, [documents?.id]);

  // Track pointer position during drag so MD headers outside dnd-kit droppables can be detected.
  useEffect(() => {
    const ownerDocument = treeRootRef.current?.ownerDocument;

    if (!activeDraggedDocument || !ownerDocument) {
      return undefined;
    }

    const handlePointerMove = (event: PointerEvent) => {
      dragPointerRef.current = {
        x: event.clientX,
        y: event.clientY,
      };

      const pointerTarget = getCurrentMeasurementDatePointerTarget();

      if (pointerTarget) {
        setActiveDropAction(pointerTarget.action);
        setActiveDropTargetLabel(pointerTarget.targetLabel);
        return;
      }

      if (activeDropActionRef.current !== 'move') {
        setActiveDropAction('move');
        setActiveDropTargetLabel(null);
      }
    };

    ownerDocument.addEventListener('pointermove', handlePointerMove);

    return () => {
      ownerDocument.removeEventListener('pointermove', handlePointerMove);
      dragPointerRef.current = null;
    };
  }, [activeDraggedDocument, getCurrentMeasurementDatePointerTarget, setActiveDropAction, setActiveDropTargetLabel]);

  // Communicate blocked MD-header drops with the native cursor while the overlay follows the pointer.
  useEffect(() => {
    const ownerDocument = treeRootRef.current?.ownerDocument;
    const body = ownerDocument?.body;

    if (!body || !activeDraggedDocument || activeDropAction !== 'blocked') {
      return undefined;
    }

    const previousCursor = body.style.cursor;
    body.style.cursor = 'not-allowed';

    return () => {
      body.style.cursor = previousCursor;
    };
  }, [activeDraggedDocument, activeDropAction]);

  const handleToggleDocument = (documentReferenceId: number, checked: boolean) =>
    canSelectDocuments ? onDocumentSelectionChange?.(documentReferenceId, checked) : undefined;

  const handleToggleDocuments = (documentReferenceIds: number[], checked: boolean) => {
    if (!canSelectDocuments) {
      return;
    }

    if (onDocumentReferencesSelectionChange) {
      onDocumentReferencesSelectionChange(documentReferenceIds, checked);
      return;
    }

    documentReferenceIds.forEach(documentReferenceId => {
      onDocumentSelectionChange?.(documentReferenceId, checked);
    });
  };

  const handleToggleFolder = (folderId: number) => {
    setClosedFolderIds(currentClosedFolderIds => {
      const nextClosedFolderIds = new Set(currentClosedFolderIds);

      if (nextClosedFolderIds.has(folderId)) {
        nextClosedFolderIds.delete(folderId);
      } else {
        nextClosedFolderIds.add(folderId);
      }

      return nextClosedFolderIds;
    });
  };

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const dragData = event.active.data.current;

      if (!isFirmDocumentDragData(dragData)) {
        return;
      }

      setActiveDraggedDocument(dragData.documentReference);
      setActiveDropAction('move');
    },
    [setActiveDropAction]
  );

  const handleDragOver = useCallback(
    (event: DragOverEvent) => {
      const pointerTarget = getCurrentMeasurementDatePointerTarget();

      if (pointerTarget) {
        setActiveDropAction(pointerTarget.action);
        setActiveDropTargetLabel(pointerTarget.targetLabel);
        return;
      }

      const dropData = event.over?.data.current;

      setActiveDropAction('move');
      setActiveDropTargetLabel(isFirmDocumentDropData(dropData) ? dropData.targetLabel : null);
    },
    [getCurrentMeasurementDatePointerTarget, setActiveDropAction, setActiveDropTargetLabel]
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveDraggedDocument(null);
      setActiveDropAction('move');
      setActiveDropTargetLabel(null);

      const dragData = event.active.data.current;
      const dropData = event.over?.data.current;

      if (!isDocumentMoveInteractionEnabled || !isFirmDocumentDragData(dragData)) {
        return;
      }

      const pointerTarget = getCurrentMeasurementDatePointerTarget();
      const sourceMeasurementDateId = getDocumentReferenceMeasurementDateId(
        dragData.documentReference,
        documents?.measurement_date?.id
      );

      if (pointerTarget?.action === 'blocked') {
        return;
      }

      if (
        pointerTarget?.action === 'copy' &&
        pointerTarget.copyTarget &&
        onDocumentCopyToMeasurementDate &&
        sourceMeasurementDateId !== pointerTarget.copyTarget.targetMeasurementDateId
      ) {
        onDocumentCopyToMeasurementDate(dragData.documentReference, pointerTarget.copyTarget);
        return;
      }

      if (!isFirmDocumentDropData(dropData)) {
        return;
      }

      if (
        sourceMeasurementDateId === dropData.targetMeasurementDateId &&
        (dragData.documentReference.folder ?? null) === dropData.targetFolderId
      ) {
        return;
      }

      Promise.resolve(
        onDocumentMove?.(dragData.documentReference, {
          targetCompanyId: dropData.targetCompanyId,
          targetFolderId: dropData.targetFolderId,
          targetLabel: dropData.targetLabel,
          targetMeasurementDateId: dropData.targetMeasurementDateId,
        })
      ).catch(() => undefined);
    },
    [
      documents?.measurement_date?.id,
      getCurrentMeasurementDatePointerTarget,
      isDocumentMoveInteractionEnabled,
      onDocumentCopyToMeasurementDate,
      onDocumentMove,
      setActiveDropAction,
      setActiveDropTargetLabel,
    ]
  );

  const handleDragCancel = useCallback(() => {
    setActiveDraggedDocument(null);
    setActiveDropAction('move');
    setActiveDropTargetLabel(null);
  }, [setActiveDropAction, setActiveDropTargetLabel]);

  if (isLoading) {
    return (
      <MeasurementDateDocumentsTreeRoot
        ref={treeRootRef}
        aria-label="Measurement date documents"
        data-testid={MEASUREMENT_DATE_DOCUMENTS_TREE_TEST_IDS.root}
        data-firm-documents-dragging={activeDraggedDocument ? 'true' : undefined}
        data-measurement-date-documents-tree-root="true"
        onKeyDown={handleItemsKeyDown}
        role="tree"
        zoomLevel={zoomLevel}>
        <MeasurementDateDocumentsTreeSkeleton />
      </MeasurementDateDocumentsTreeRoot>
    );
  }

  if (isError) {
    return (
      <MeasurementDateDocumentsTreeRoot
        ref={treeRootRef}
        aria-label="Measurement date documents"
        data-testid={MEASUREMENT_DATE_DOCUMENTS_TREE_TEST_IDS.root}
        data-firm-documents-dragging={activeDraggedDocument ? 'true' : undefined}
        data-measurement-date-documents-tree-root="true"
        onKeyDown={handleItemsKeyDown}
        role="tree"
        zoomLevel={zoomLevel}>
        <MeasurementDateDocumentsTreeStateMessage
          description={errorDescription}
          testId={MEASUREMENT_DATE_DOCUMENTS_TREE_TEST_IDS.errorState}
          title={errorTitle}
        />
      </MeasurementDateDocumentsTreeRoot>
    );
  }

  if (!documentTree.totalFileCount && !documentTree.totalFolderCount) {
    return (
      <MeasurementDateDocumentsTreeRoot
        ref={treeRootRef}
        aria-label="Measurement date documents"
        data-testid={MEASUREMENT_DATE_DOCUMENTS_TREE_TEST_IDS.root}
        data-measurement-date-documents-tree-root="true"
        onKeyDown={handleItemsKeyDown}
        role="tree"
        zoomLevel={zoomLevel}>
        <MeasurementDateDocumentsTreeStateMessage
          actions={emptyStateActions}
          description={emptyDescription}
          testId={MEASUREMENT_DATE_DOCUMENTS_TREE_TEST_IDS.emptyState}
          title={emptyTitle}
        />
      </MeasurementDateDocumentsTreeRoot>
    );
  }

  return (
    <DndContext
      collisionDetection={firmDocumentTreeCollisionDetection}
      onDragCancel={isDocumentMoveInteractionEnabled ? handleDragCancel : undefined}
      onDragEnd={isDocumentMoveInteractionEnabled ? handleDragEnd : undefined}
      onDragOver={isDocumentMoveInteractionEnabled ? handleDragOver : undefined}
      onDragStart={isDocumentMoveInteractionEnabled ? handleDragStart : undefined}
      sensors={sensors}>
      <MeasurementDateDocumentsTreeRoot
        ref={treeRootRef}
        aria-label="Measurement date documents"
        data-testid={MEASUREMENT_DATE_DOCUMENTS_TREE_TEST_IDS.root}
        data-measurement-date-documents-tree-root="true"
        onKeyDown={handleItemsKeyDown}
        role="tree"
        zoomLevel={zoomLevel}>
        <MeasurementDateDocumentsTreeRootDropZone
          isVisible={Boolean(activeDraggedDocument?.folder && !hasCompanyGroups)}
          measurementDateId={treeMeasurementDateId}
          targetLabel={rootDropTargetLabel}
        />
        {documentTree.filesWithoutFolder.length ? (
          <MeasurementDateDocumentsTreeGroup data-testid={MEASUREMENT_DATE_DOCUMENTS_TREE_TEST_IDS.filesWithoutFolder}>
            {documentTree.filesWithoutFolder.map(documentReference => (
              <MeasurementDateDocumentsTreeFileRow
                key={documentReference.document_reference_id}
                actionInProgress={actionInProgress}
                activeDocumentReferenceId={activeDocumentReferenceId}
                canCopyDocuments={canCopyDocuments}
                canDownloadDocuments={canDownloadDocuments}
                canManageDocuments={canManageDocuments}
                canMoveDocuments={isDocumentMoveInteractionEnabled}
                canSelectDocuments={canSelectDocuments}
                depth={0}
                documentReference={documentReference}
                downloadingDocumentReferenceId={downloadingDocumentReferenceId}
                isHighlighted={highlightedDocumentReferenceId === documentReference.document_reference_id}
                isSelected={selectedDocumentReferenceIds.has(documentReference.document_reference_id)}
                onCopy={onDocumentCopy}
                onDelete={onDocumentDelete}
                onDownload={onDocumentDownload}
                onPreviewSelect={onDocumentPreviewSelect}
                onRename={onDocumentRename}
                searchHighlightText={searchHighlightText}
                onToggle={handleToggleDocument}
              />
            ))}
          </MeasurementDateDocumentsTreeGroup>
        ) : null}
        {documentTree.folders.map(folder => (
          <MeasurementDateDocumentsTreeFolder
            key={folder.id}
            depth={0}
            folder={folder}
            actionInProgress={actionInProgress}
            activeDocumentReferenceId={activeDocumentReferenceId}
            canCopyDocuments={canCopyDocuments}
            canDownloadDocuments={canDownloadDocuments}
            canManageDocuments={canManageDocuments}
            canMoveDocuments={isDocumentMoveInteractionEnabled}
            canSelectDocuments={canSelectDocuments}
            downloadingDocumentReferenceId={downloadingDocumentReferenceId}
            highlightedDocumentReferenceId={highlightedDocumentReferenceId}
            highlightedFolderId={highlightedFolderId}
            closedFolderIds={closedFolderIds}
            isOpen={!closedFolderIds.has(folder.id)}
            onDocumentDelete={onDocumentDelete}
            onDocumentDownload={onDocumentDownload}
            onDocumentCopy={onDocumentCopy}
            onDocumentPreviewSelect={onDocumentPreviewSelect}
            onDocumentRename={onDocumentRename}
            onFolderAddSubfolder={onFolderAddSubfolder}
            onFolderDelete={onFolderDelete}
            onFolderRename={onFolderRename}
            onFolderUploadDocument={onFolderUploadDocument}
            onToggleDocument={handleToggleDocument}
            onToggleFolder={handleToggleFolder}
            onToggleFolderDocuments={handleToggleDocuments}
            searchHighlightText={searchHighlightText}
            selectedDocumentReferenceIds={selectedDocumentReferenceIds}
            measurementDateLabel={measurementDateDropTargetLabel}
          />
        ))}
        {hasMoreDocuments && onShowMoreDocuments && remainingDocumentCount > 0 ? (
          <MeasurementDateDocumentsTreeShowMoreButton
            data-testid={MEASUREMENT_DATE_DOCUMENTS_TREE_TEST_IDS.showMoreDocuments}
            loading={isShowMoreDocumentsLoading}
            loadingBehavior="inline"
            onClick={onShowMoreDocuments}
            visualVariant="primaryGhost">
            {MEASUREMENT_DATE_DOCUMENTS_TREE_LABELS.showMoreDocuments(remainingDocumentCount)}
          </MeasurementDateDocumentsTreeShowMoreButton>
        ) : null}
      </MeasurementDateDocumentsTreeRoot>
      <DragOverlay
        adjustScale={false}
        dropAnimation={{
          duration: 140,
          easing: 'cubic-bezier(0.2, 0, 0, 1)',
        }}
        zIndex={1400}>
        {activeDraggedDocument ? (
          <MeasurementDateDocumentsTreeDragOverlayContent
            documentReference={activeDraggedDocument}
            dropAction={activeDropAction}
            dropTargetLabel={activeDropTargetLabel}
            zoomLevel={zoomLevel}
          />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

export default MeasurementDateDocumentsTree;
